import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { HttpTransport, installApi, MockTransport } from './api';
import { AuthProvider, readStoredToken } from './contexts/auth';
import { ConfirmProvider } from './contexts/confirm';
import { ThemeProvider } from './contexts/theme';
import { ToastProvider } from './contexts/toast';
// Plan AE 2.3: the font ships with the site (one variable file per script) instead of from Google.
import './fonts.css';
import './index.css';
import { router } from './routes/router';
import { seedPublicBoot } from './services/public-queries';

/**
 * Swapping the backend happens HERE and nowhere else — no screen imports a
 * transport. The mock is opt-in only; it is never a silent fallback, because
 * a misconfigured deploy should look like a config error, not a data bug.
 */
const transport =
  import.meta.env.VITE_USE_MOCK === '1'
    ? new MockTransport()
    : // The API lives on the same origin: one Worker serves the SPA and /api.
      new HttpTransport('/api', readStoredToken);

installApi(transport);

/**
 * After a deploy, a tab opened on the old version asks for code files that no
 * longer exist (their names carry a content hash). Reload once to pick up the
 * new version instead of showing an error; the flag stops a reload loop if
 * the new version is broken too.
 */
window.addEventListener('vite:preloadError', (event) => {
  try {
    if (sessionStorage.getItem('hbac-reloaded-for-update')) return;
    sessionStorage.setItem('hbac-reloaded-for-update', '1');
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});
window.addEventListener('load', () => {
  try {
    // A successful load means the page is current; allow the next update to reload.
    window.setTimeout(() => sessionStorage.removeItem('hbac-reloaded-for-update'), 10_000);
  } catch {
    /* storage blocked: nothing to reset */
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The app is a dashboard people leave open; refetching on every window
      // focus turns that into constant background traffic.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});
// A public page's HTML may carry its first data (Plan AE 4.1): into the cache before the first render.
seedPublicBoot(queryClient);


const container = document.getElementById('root');
if (!container) throw new Error('#root missing from index.html');

createRoot(container).render(
  <ThemeProvider>
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  </ThemeProvider>,
);
